import { MAX_TIMESTAMP, MIN_TIMESTAMP } from '@norma/shared/helpers/formatTime';
import {
  PrejudiceFormCalendrierAssistanceCapitalisation,
  PrejudiceType,
} from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import i18next from 'i18next';
import { omit } from 'lodash';
import {
  validationSchemaPrejudiceBase,
  validationSchemaPrejudiceFormCapitalisationOrRenteBase,
  validationSchemaTiersPayeursCapitalisationObject,
} from 'src/constants/prejudice/validation';
import { dateString } from 'src/helpers/yup';
import * as yup from 'yup';
import { validationSchemaCalendrierAssistanceRow } from './FormCalendrierAssistance.validation';

export const validationSchemaCalendrierAssistanceCapitalisation = ({
  procedure,
  victime,
  prejudiceType,
  dateAccident,
  dateConsolidation,
  dateLiquidation,
  dateDeces,
}: {
  procedure: Procedure;
  victime: Victime;
  prejudiceType: PrejudiceType;
  dateAccident: Date | undefined;
  dateConsolidation: Date | undefined;
  dateLiquidation: Date | undefined;
  dateDeces: Date | undefined;
}): yup.ObjectSchema<PrejudiceFormCalendrierAssistanceCapitalisation> => {
  const normaMinDate = new Date(MIN_TIMESTAMP);
  const normaMaxDate = new Date(MAX_TIMESTAMP);
  return validationSchemaPrejudiceBase
    .concat(validationSchemaPrejudiceFormCapitalisationOrRenteBase)
    .shape({
      victimeSommeACapitaliser: yup.number().min(0).required(),
      ageDernierArrerage: yup.lazy((value) =>
        value === ''
          ? (yup
              .string()
              .optional()
              .transform(() => null) as unknown as yup.NumberSchema<
              number,
              yup.AnyObject,
              undefined,
              ''
            >)
          : yup.number().optional().nullable().min(0),
      ),
      isLastArrerageViager: yup.boolean().optional(),
      baremeCapitalisation: yup.string().optional(),
      victimeCoefficientCapitalisation: yup
        .number()
        .nullable()
        .defined()
        .min(0),
      capitalisationTiersPayeurs: yup.object(
        omit(validationSchemaTiersPayeursCapitalisationObject(), [
          'montantCapitalise',
        ]),
      ),
      tiersPayeursTotalCapitalise: yup.number().required().min(0),
      totalMontantTotalAnnualise: yup.number().defined(),
      commentaires: yup.string().optional().defined(),
      rows: yup
        .array()
        .defined()
        .of(
          validationSchemaCalendrierAssistanceRow({
            procedure,
            victime,
            prejudiceType,
          }).shape({
            dateDebut: yup
              .string()
              .isDateString()
              .nullable()
              .required()
              .when(['type'], ([type], schema) => {
                if (!dateLiquidation) {
                  return schema;
                }
                if (type === 'aEchoir') {
                  return schema.minDate(
                    undefined,
                    i18next.t(
                      'validation.prejudices.dates.minDateLiquidation',
                    ) || '',
                    dateLiquidation,
                  );
                } else if (type === 'echu') {
                  return schema.maxDate(
                    undefined,
                    i18next.t(
                      'validation.prejudices.dates.maxDateLiquidation',
                    ) || '',
                    dateLiquidation,
                  );
                }
                return schema;
              }),
            dateFin: yup
              .string()
              .isDateString()
              .nullable()
              .optional()
              .defined()
              .minDate(
                'dateDebut',
                i18next.t('validation.prejudices.dates.minDateDebut') || '',
              )
              .when(['type'], ([type], schema) => {
                const schemaWithRequired =
                  type === 'echu' ? schema.required() : schema;
                if (!dateLiquidation) {
                  return schemaWithRequired;
                }
                if (type === 'aEchoir') {
                  return schemaWithRequired.minDate(
                    undefined,
                    i18next.t(
                      'validation.prejudices.dates.minDateLiquidation',
                    ) || '',
                    dateLiquidation,
                  );
                } else if (type === 'echu') {
                  return schemaWithRequired.maxDate(
                    undefined,
                    i18next.t(
                      'validation.prejudices.dates.maxDateLiquidation',
                    ) || '',
                    dateLiquidation,
                  );
                }
                return schema;
              }),
            dateJustificatif: dateString()
              .nullable()
              .optional()
              .defined()
              .minDate(
                undefined,
                dateAccident
                  ? i18next.t('validation.prejudices.dates.minDateAccident')
                  : i18next.t('validation.dates.dateTooLow'),
                dateAccident || normaMinDate,
              )
              .maxDate(
                undefined,
                dateConsolidation || dateDeces || dateLiquidation
                  ? i18next.t(
                      `validation.prejudices.dates.${
                        dateDeces
                          ? 'maxDateDeces'
                          : dateLiquidation
                            ? 'maxDateLiquidation'
                            : dateConsolidation
                              ? 'maxDateConsolidation'
                              : ''
                      }`,
                    )
                  : i18next.t('validation.dates.dateTooHigh'),
                dateDeces ||
                  dateLiquidation ||
                  dateConsolidation ||
                  normaMaxDate,
              ),
            resteACharge: yup.number().required().min(0),
          }),
        ),
      forfaitHoraire: yup.number().nullable().defined(),
      victimeTotalCapitalise: yup.number().required(),
      ageDateAttribution: yup.number().optional(),
      revalorisationCoefficientsType: yup
        .string()
        .oneOf(['annuel', 'smic'])
        .required(),
    });
};
